import { get, post, remove, update } from '@apis/api';
import { CommonResponse } from '@models/api';
import {
  MyHeartBrand,
  MyHeartBrandListResponse,
  MyHeartContentsInfo,
  MyHeartContentsListResponse,
  MyHeartItem,
  MyHeartItemInfoResponse,
  MyHeartItemListResponse,
  MyHeartProductTabItem,
  MyHeartBrandInfo,
  MyHeartMyPageContentTabContentItem,
  MyHeartCollectionContent,
  MyHeartContentItem,
} from '@wconcept/wconcept-common/models/renewal/myHeart';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8', // 필요에 따라 헤더 수정
    'MYHEART-API-KEY': process.env.NEXT_PUBLIC_MY_HEART_API_GATEWAY_KEY || '',
  },
};

/**
 * 마이하트 상품 조회
 */
export const requestGetMyHeartItems = async () => {
  const { data } = await get<CommonResponse<MyHeartItemListResponse>>({
    url: `/myheart/api/product/v2/products`,
    baseUrl: baseURL,
    options: {
      ...options,
      next: {
        revalidate: 0,
      },
      cache: 'no-store',
    },
  });
  return data?.productHeart.productCdList;
};

/**
 * 마이하트 브랜드 조회
 */
export const requestGetBrandMyHeart = async () => {
  const { data } = await get<CommonResponse<MyHeartBrandListResponse>>({
    url: `/myheart/api/brand/v2/brands`,
    baseUrl: baseURL,
    options: {
      ...options,
      next: {
        revalidate: 0,
      },
      cache: 'no-store',
    },
  });
  return data?.brandHeart.brandCdList;
};

/**
 * 마이하트 컨텐츠 조회
 * 전체 조회(ALL), 기존 기획전(ISSUE), 브랜드 스토리(BRAND_STORY), 스타일링(STYLING), W 시리즈(W_SERIES), 룩북(LOOKBOOK), 신규 기획전(EVENT)
 */
export const requestGetContentsMyHeart = async () => {
  const { data } = await get<CommonResponse<MyHeartContentsListResponse>>({
    url: `/myheart/api/content/v2/contents`,
    baseUrl: baseURL,
    options,
  });
  return data?.contentHeart?.contentList;
};

/**
 * MyHeart Item 추가/삭제.
 */
export interface MyHeartItemRequestParam {
  itemCd: string;
  brandCd: number;
  eventId: string;
  lastKeyword: string;
}

export const requestAddMyHeartItem = async (body: MyHeartItemRequestParam) => {
  const { result } = await post<
    CommonResponse<MyHeartItemInfoResponse>,
    MyHeartItemRequestParam
  >({
    url: `/myheart/api/product/v2/product/${body.itemCd}`,
    data: body,
    baseUrl: baseURL,
    options: {
      ...options,
      next: {
        revalidate: 0,
      },
      cache: 'no-store',
    },
  });

  return result;
};

export const requestDeleteMyHeartItem = async (
  body: MyHeartItemRequestParam
) => {
  const { result } = await remove<CommonResponse>({
    url: `/myheart/api/product/v2/product/${body.itemCd}`,
    baseUrl: baseURL,
    options,
  });
  return result;
};

/**
 * MyHeart Brand 추가/삭제
 */

export interface MyHeartBrandRequestParam {
  brandCd: string;
}

export interface MyHeartBrandResponse extends CommonResponse {
  data: MyHeartBrand;
}
export const requestAddMyHeartBrand = async (
  body: MyHeartBrandRequestParam
) => {
  const { result } = await post<CommonResponse<MyHeartBrandResponse>>({
    url: `/myheart/api/brand/v2/brand/${body.brandCd}`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return result;
};

export const requestDeleteMyHeartBrand = async (
  body: MyHeartBrandRequestParam
) => {
  const { result } = await remove<CommonResponse>({
    url: `/myheart/api/brand/v2/brand/${body.brandCd}`,
    baseUrl: baseURL,
    options,
  });
  return result;
};

/**
 * MyHeart Content 추가/삭제
 */
export interface MyHeartContentRequestParam {
  custNo: string;
  contentType: 'BRAND_STORY' | 'STYLE_CLIP' | 'W_SERIES' | 'LOOKBOOK' | 'EVENT';
  contentId: string;
  profileSeqNo?: number;
}
export interface MyHeartContentResponse {
  data: MyHeartContentsInfo;
}

export const requestAddMyHeartContents = async (
  body: MyHeartContentRequestParam
) => {
  const { result } = await post<CommonResponse<MyHeartContentResponse>>({
    url: `/myheart/api/content/v2/content/${body.contentType}/${body.contentId}`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return result;
};

export const requestDeleteMyHeartContents = async (
  body: MyHeartContentRequestParam
) => {
  const { result } = await remove<CommonResponse>({
    url: `/myheart/api/content/v2/content/${body.contentType}/${body.contentId}`,
    baseUrl: baseURL,
    options,
  });
  return result;
};

// 마이하트 페이지 관련

export type MyHeartCollectionResponse = {
  custNo: string;
  collectionList: MyHeartCollectionContent[];
};

export interface MyHeartCollectionRequestParam {
  collectionName?: string;
  collectionId?: string;
  productList?: string[];
}

/**
 * 컬렉션 조회 API
 */
export const requestGetMyHeartCollection = async () => {
  const { data } = await get<
    CommonResponse<{
      custNo: number;
      collectionList: MyHeartCollectionContent[];
    }>
  >({
    url: `/myheart/api/collection/v2/collection`,
    baseUrl: baseURL,
    options,
  });
  return data;
};

/**
 * 컬렉션 생성 API
 */
export const requestAddMyHeartCollection = async (
  body: MyHeartCollectionRequestParam
) => {
  const { data } = await post<
    CommonResponse<MyHeartCollectionResponse>,
    MyHeartCollectionRequestParam
  >({
    url: `/myheart/api/collection/v2/collection`,
    data: body,
    baseUrl: baseURL,
    options,
  });
  return data;
};

/**
 * 컬렉션 상품 추가 API
 */
export const requestAddMyHeartCollectionProduct = async (
  body: MyHeartCollectionRequestParam
) => {
  const { data } = await post<CommonResponse>({
    url: `/myheart/api/collection/v2/collection/${body.collectionId}/product`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

/**
 * 컬렉션 컨텐츠 삭제 API
 */
export const requestDeleteMyHeartCollectionContent = async (
  body: MyHeartCollectionRequestParam
) => {
  const { data } = await remove<CommonResponse>({
    url: `/myheart/api/collection/v2/collection/${body.collectionId}`,
    baseUrl: baseURL,
    options,
  });

  return data;
};

/**
 * 컬렉션 컨텐츠 수정 API
 */
export const requestUpdateMyHeartCollectionName = async (
  body: MyHeartCollectionRequestParam
) => {
  const { data } = await update<
    CommonResponse<MyHeartCollectionResponse>,
    MyHeartCollectionRequestParam
  >({
    url: `/myheart/api/collection/v2/collection/${body.collectionId}`,
    data: body,
    baseUrl: baseURL,
    options,
  });
  return data;
};

/**
 * 디렉토리 상품 삭제 API
 */
export const requestDeleteMyHeartDirectoryProduct = async (
  body: MyHeartCollectionRequestParam
) => {
  const { data } = await update<CommonResponse>({
    url: `/myheart/api/collection/v2/collection/${body.collectionId}/product`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

export interface MyHeartMyPageProductRequestParam {
  pageNo?: number;
  offset?: number;
  pageSize?: number;
  tabId?: string;
  tabType?: string;
  sortType?: number;
  includeSoldOut?: boolean;
  itemCdList?: string[];
}

export interface MyHeartMyPageProductTabProductResponse {
  total: number;
  totalPages: number;
  pageSize: number;
  page: number;
  productList: Product[];
}

export interface MyHeartMyPageBrandRequestParam {
  pageNo?: number;
  offset?: number;
  pageSize?: number;
  sortType?: number;
  brandCdList?: string[];
}

// 마이페이지 상품 탭 조회 API
export const requestGetMyHeartMyPageProductTab = async (
  body: MyHeartMyPageProductRequestParam
) => {
  const { data } = await post<
    CommonResponse<{ tabList: MyHeartProductTabItem[] }>
  >({
    url: `/myheart/api/mypage/v2/product/tab`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

// 마이페이지 상품 리스트 조회 API
export const requestGetMyHeartMyPageProductList = async (
  body: MyHeartMyPageProductRequestParam
) => {
  const { data } = await post<
    CommonResponse<MyHeartMyPageProductTabProductResponse>,
    MyHeartMyPageProductRequestParam
  >({
    url: `/myheart/api/mypage/v2/product/products`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

// 마이페이지 상품 리스트 조회 API (컬렉션 상품 추가)
export const requestGetMyHeartMyPageCollectionAddProductList = async (
  body: MyHeartMyPageProductRequestParam
) => {
  const { data } = await post<
    CommonResponse<MyHeartMyPageProductTabProductResponse>,
    MyHeartMyPageProductRequestParam
  >({
    url: `/myheart/api/mypage/v2/product/collection/products`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

// 마이페이지 브랜드 리스트 조회 API
export const requestGetMyHeartMyPageBrandList = async (
  body: MyHeartMyPageBrandRequestParam
) => {
  const { data } = await post<
    CommonResponse<MyHeartMyPageBrandTabBrandResponse>,
    MyHeartMyPageBrandRequestParam
  >({
    url: `/myheart/api/mypage/v2/brand/brands`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

// 마이페이지 인기 브랜드 리스트 조회 API
export const requestGetMyHeartMyPagePopularBrandList = async () => {
  const { data } = await get<CommonResponse<MyHeartBrandInfo[]>>({
    url: `/myheart/api/mypage/v2/brand/popular`,
    baseUrl: baseURL,
    options,
  });

  return data;
};

export interface MyHeartMyPageBrandTabBrandResponse {
  total: number;
  totalPages: number;
  pageSize: number;
  page: number;
  recommendBrandList: MyHeartBrandInfo[];
}

// 마이페이지 연관 브랜드 리스트 조회 API
export const requestGetMyHeartMyPageRelatedBrandList = async (
  body: MyHeartMyPageBrandRequestParam
) => {
  const { data } = await post<CommonResponse<MyHeartBrandInfo[]>>({
    url: `/myheart/api/mypage/v2/brand/relate`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

export interface MyHeartContentTabItem {
  tabName: string;
  tabType: string;
}
export interface MyHeartContentTabListResponse {
  tabList: MyHeartContentTabItem[];
}

// 마이페이지 컨텐츠 탭 조회 API
export const requestGetMyHeartMyPageContentsTabList = async () => {
  const { data } = await get<CommonResponse<MyHeartContentTabListResponse>>({
    url: `/myheart/api/mypage/v2/content/tab`,
    baseUrl: baseURL,
    options,
  });

  return data;
};

export interface MyHeartMyPageContentRequestParam {
  custNo: string;
  offset?: number;
  pageNo?: number;
  pageSize?: number;
  tabType?: string;
  contentList?: MyHeartContentItem[];
}

export interface MyHeartMyPageContentTabContentResponse {
  total: number;
  totalPages: number;
  pageSize: number;
  page: number;
  contentList: MyHeartMyPageContentTabContentItem[];
}

// 마이페이지 컨텐츠 리스트 조회 API
export const requestGetMyHeartMyPageContentList = async (
  body: MyHeartMyPageContentRequestParam
) => {
  const { data } = await post<
    CommonResponse<MyHeartMyPageContentTabContentResponse>
  >({
    url: `/myheart/api/mypage/v2/content/contents`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

export interface MyHeartMyPageCountResponse {
  productHeartCount: number;
  brandHeartCount: number;
  contentHeartCount: number;
}

// 유저의 마이하트 Count 조회 API
export const requestGetMyHeartMyPageCount = async () => {
  const { data } = await get<CommonResponse<MyHeartMyPageCountResponse>>({
    url: `/myheart/api/count/v2/customer`,
    baseUrl: baseURL,
    options,
  });

  return data;
};

export interface MyHeartBrandCountResponse {
  brandCd: string;
  productHeartCount: number;
  productViewCount: number;
}

// 유저의 마이하트 Count 조회 API
export const requestGetMyHeartBrandCount = async (brandCd: string) => {
  const { data } = await get<CommonResponse<MyHeartBrandCountResponse>>({
    url: `/myheart/api/count/v2/brand/${brandCd}/aggregation`,
    baseUrl: baseURL,
    options,
  });

  return data;
};

export interface MyHeartMyPageRecentlyHeartResponse {
  contentId: string;
  contentStatus: string;
  contentType: string;
  contentTitle1: string;
  contentTitle2: string;
  profileSeqNo: number;
  nickName: string;
  vodYn: string;
  brandCd: number;
  landingUrl: string;
  contentImageUrl: string;
}

// 마이페이지 인기 상품 리스트 조회 API
export const requestGetMyHeartMyPagePopularProducts = async () => {
  const { data } = await get<CommonResponse<Product[]>>({
    url: `/myheart/api/mypage/v2/product/popular`,
    baseUrl: baseURL,
    options,
  });

  return data;
};

// 마이페이지 연관 상품 리스트 조회 API
export const requestGetMyHeartMyPageRelationProductList = async (
  body: MyHeartMyPageProductRequestParam
) => {
  const { data } = await post<CommonResponse<Product[]>>({
    url: `/myheart/api/mypage/v2/product/relate`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

// 마이페이지 최근 많이 하트한 리스트 조회 API
export const requestGetMyHeartMyPagePopularContentList = async () => {
  const { data } = await get<
    CommonResponse<MyHeartMyPageContentTabContentItem[]>
  >({
    url: `/myheart/api/mypage/v2/content/popular`,
    baseUrl: baseURL,
    options,
  });

  return data;
};

// 마이페이지 최근 마이하트 컨텐츠와 연관있는 리스트 조회 API
export const requestGetMyHeartMyPageRelationContentList = async (
  body: MyHeartMyPageContentRequestParam
) => {
  const { data } = await post<
    CommonResponse<MyHeartMyPageContentTabContentItem[]>
  >({
    url: `/myheart/api/mypage/v2/content/relate`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};
